import React, { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaCheckCircle, FaClock, FaTimesCircle, FaUndo } from 'react-icons/fa';
import { approvalFlowAPI } from '../services/api';

// Level-wise approval trail + the "Approve" action for one record, driven by
// the server's Approval Flow (Settings → Approval Flow). The server decides who
// may sign the current level; this panel only shows the button to them.
//
//   <ApprovalPanel workflowType="risk_assessment.approval" entityId={id}
//                  pending={status is a pending status} refreshKey={status}
//                  onSign={(level) => openESign('approve', level)} />
const DECISION_STYLE = {
  APPROVED: { icon: FaCheckCircle, color: 'var(--success-color, #16a34a)', label: 'Approved' },
  RETURNED: { icon: FaUndo, color: 'var(--warning-color, #d97706)', label: 'Returned' },
  REJECTED: { icon: FaTimesCircle, color: 'var(--danger-color, #dc2626)', label: 'Rejected' },
};

const ApprovalPanel = ({ workflowType, entityId, pending, refreshKey, onSign, compact = false }) => {
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    if (!workflowType || !entityId) return;
    try {
      const res = await approvalFlowAPI.getState(workflowType, entityId);
      setState(res.data.data);
    } catch {
      setState(null);
    }
  }, [workflowType, entityId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!state || !state.enabled) return null;
  const { levels, steps, current_level: current, request_status: reqStatus, can_act: canAct } = state;
  const isOpen = pending && (reqStatus === 'PENDING' || reqStatus === null);
  const stepFor = (level) => [...steps].reverse().find((s) => s.level === level);

  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: compact ? '8px 10px' : '10px 14px', marginBottom: '12px', background: 'var(--card-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {levels.map((lvl, i) => {
            const step = stepFor(lvl.level);
            const done = step && reqStatus !== 'CANCELLED' && (step.decision === 'APPROVED' || reqStatus !== 'PENDING' || lvl.level < current);
            const style = done ? DECISION_STYLE[step.decision] : null;
            const isCurrent = isOpen && lvl.level === (current || 1);
            const Icon = style ? style.icon : FaClock;
            return (
              <React.Fragment key={lvl.level}>
                {i > 0 && <span style={{ color: 'var(--text-secondary)' }}>→</span>}
                <span
                  title={`${lvl.level_label}: ${lvl.roles.join(', ')}${lvl.user_ids.length ? ` + ${lvl.user_ids.length} named user(s)` : ''}${step ? `\n${style?.label || step.decision} by ${step.approved_by_name} on ${new Date(step.decided_at).toLocaleString()}${step.comments ? ` — "${step.comments}"` : ''}` : ''}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', borderRadius: '999px', fontSize: 'var(--fs-sm)',
                    border: `1px solid ${isCurrent ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    color: style ? style.color : isCurrent ? 'var(--primary-color)' : 'var(--text-secondary)',
                    fontWeight: isCurrent ? 700 : 500,
                  }}
                >
                  <Icon /> L{lvl.level} · {lvl.level_label}
                  {done && step?.approved_by_name ? <span style={{ fontWeight: 400 }}>({step.approved_by_name})</span> : null}
                </span>
              </React.Fragment>
            );
          })}
        </div>
        {isOpen && canAct && onSign && (
          <button type="button" className="btn btn-success btn-sm" onClick={() => onSign({ level: current || 1, label: state.current_level_label })} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaCheck /> Review · Level {current || 1}: {state.current_level_label}
          </button>
        )}
      </div>
      {isOpen && !canAct && state.waiting_on && (
        <div style={{ marginTop: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
          Waiting for Level {current || 1} ({state.current_level_label}): {state.waiting_on}
        </div>
      )}
    </div>
  );
};

export default ApprovalPanel;
